/**
 * Stay AI Subscription Widget Integration
 * Updates product price display and add-to-cart button when subscription options change.
 */
(function() {
  'use strict';

  const CONFIG = {
    productPriceSelector: 'product-price',
    addToCartPriceSelector: '[ref="addToCartPrice"], .add-to-cart-price',
    priceContainerSelector: '[ref="priceContainer"]',
    priceRegex: /\$[\d,]+\.?\d*/g
  };

  function parsePrice(priceString) {
    if (!priceString) return 0;
    return parseFloat(priceString.replace(/[^0-9.]/g, '')) || 0;
  }

  function extractPricesFromOption(element) {
    let price = null;
    let compareAtPrice = null;

    const radioValue = (element.value || '').toLowerCase();
    const radioId = (element.id || '').toLowerCase();
    const labelText = (element.closest('label')?.textContent || '').toLowerCase();

    const isOneTimeOption = radioValue.includes('onetime') || radioId.includes('onetime') || labelText.includes('one-time');
    const isSubscriptionOption = radioValue.includes('subscription') || radioId.includes('subscription') || labelText.includes('subscribe');

    const oneTimePriceElement = document.querySelector('[data-stayai-onetime-price]');
    const subscriptionPriceElement = document.querySelector('[data-stayai-subscription-price]');

    if (isOneTimeOption && oneTimePriceElement) {
      const priceMatch = oneTimePriceElement.textContent.trim().match(CONFIG.priceRegex);
      if (priceMatch) price = priceMatch[0];
    } else if (isSubscriptionOption && subscriptionPriceElement) {
      const allPrices = subscriptionPriceElement.textContent.trim().match(CONFIG.priceRegex) || [];

      if (allPrices.length >= 2) {
        const price1 = parsePrice(allPrices[0]);
        const price2 = parsePrice(allPrices[1]);
        if (price1 > price2) {
          compareAtPrice = allPrices[0];
          price = allPrices[1];
        } else {
          compareAtPrice = allPrices[1];
          price = allPrices[0];
        }
      } else if (allPrices.length === 1) {
        price = allPrices[0];
      }

      // Use one-time price as compare-at if not found
      if (!compareAtPrice && oneTimePriceElement) {
        const oneTimePriceMatch = oneTimePriceElement.textContent.trim().match(CONFIG.priceRegex);
        if (oneTimePriceMatch && price) {
          const oneTimeValue = parsePrice(oneTimePriceMatch[0]);
          const subscriptionValue = parsePrice(price);
          if (oneTimeValue > subscriptionValue) {
            compareAtPrice = oneTimePriceMatch[0];
          }
        }
      }
    }

    // Fallback: parse from container text
    if (!price) {
      const text = (element.closest('label, div')?.textContent || '');
      const prices = text.match(CONFIG.priceRegex) || [];
      if (prices.length >= 1) price = prices[prices.length - 1];
    }

    return { price, compareAtPrice };
  }

  function isSubscriptionOption(element) {
    const text = (element.closest('label, div')?.textContent || '').toLowerCase();
    const value = (element.value || '').toLowerCase();
    return text.includes('subscribe') || text.includes('subscription') || value.includes('subscription');
  }

  function updateProductPrice(price, compareAtPrice) {
    const productPriceElement = document.querySelector(CONFIG.productPriceSelector);
    if (!productPriceElement) return;

    if (typeof productPriceElement.updatePriceDisplay === 'function') {
      productPriceElement.updatePriceDisplay(price, compareAtPrice);
      return;
    }

    const priceContainer = productPriceElement.querySelector(CONFIG.priceContainerSelector);
    if (!priceContainer) {
      const priceSpan = productPriceElement.querySelector('.price');
      if (priceSpan) priceSpan.textContent = price;
      return;
    }

    if (compareAtPrice) {
      priceContainer.innerHTML = `
        <span role="group">
          <span class="visually-hidden">Regular price&nbsp;</span>
          <span class="compare-at-price">${compareAtPrice}</span>
        </span>
        <span role="group">
          <span class="visually-hidden">Sale price&nbsp;</span>
          <span class="price">${price}</span>
        </span>
      `;
    } else {
      priceContainer.innerHTML = `<span class="price">${price}</span>`;
    }
  }

  function updateAddToCartPrice(price) {
    const priceElements = document.querySelectorAll(CONFIG.addToCartPriceSelector);
    priceElements.forEach(el => el.textContent = price);
  }

  function handleOptionChange(selectedOption) {
    if (!selectedOption?.checked) return;

    const { price, compareAtPrice } = extractPricesFromOption(selectedOption);
    if (!price) return;

    const showCompareAt = isSubscriptionOption(selectedOption) && compareAtPrice;
    updateProductPrice(price, showCompareAt ? compareAtPrice : null);
    updateAddToCartPrice(price);
  }

  function findSubscriptionRadios() {
    return Array.from(document.querySelectorAll('input[type="radio"]')).filter(radio => {
      const text = (radio.closest('label, div')?.textContent || '').toLowerCase();
      return text.includes('purchase') || text.includes('subscribe') || text.includes('one-time');
    });
  }

  function processInitialSelection() {
    const radios = findSubscriptionRadios();
    const selectedRadio = radios.find(r => r.checked);
    if (selectedRadio) {
      handleOptionChange(selectedRadio);
    }
  }

  function setupListeners() {
    const radios = findSubscriptionRadios();

    radios.forEach(radio => {
      radio.addEventListener('change', () => handleOptionChange(radio));
    });

    // Click delegation for dynamically added elements
    document.addEventListener('click', (event) => {
      const radio = event.target.closest('input[type="radio"]');
      if (!radio) return;

      const text = (radio.closest('label, div')?.textContent || '').toLowerCase();
      if (text.includes('purchase') || text.includes('subscribe') || text.includes('one-time')) {
        setTimeout(() => handleOptionChange(radio), 10);
      }
    });

    // Process initially selected option
    processInitialSelection();
  }

  function normalizePlanId(value) {
    if (value == null) return '';
    const str = String(value);
    const gid = str.match(/SellingPlan\/(\d+)/i);
    if (gid) return gid[1];
    if (/^\d+$/.test(str)) return str;
    return '';
  }

  function getHiddenPlansConfig() {
    const el = document.getElementById('stay-ai-pdp-hidden-plans');
    if (!el) return null;

    try {
      const data = JSON.parse(el.textContent);
      const planIds = (data.planIds || []).map(normalizePlanId).filter(Boolean);
      if (!planIds.length) return null;

      return {
        planIds: new Set(planIds),
        allowedPlanIds: (data.allowedPlanIds || []).map(normalizePlanId).filter(Boolean),
        groupNames: (data.groupNames || []).map((name) => String(name).toLowerCase()).filter(Boolean),
      };
    } catch (err) {
      console.warn('Stay AI PDP plan filter: invalid JSON', err);
      return null;
    }
  }

  function valueMatchesPlanIds(value, planIds) {
    const normalized = normalizePlanId(value);
    if (normalized && planIds.has(normalized)) return true;

    const raw = String(value || '');
    if (!raw) return false;

    for (const id of planIds) {
      if (raw.includes(id)) return true;
    }
    return false;
  }

  function elementMatchesHiddenPlan(el, config) {
    const attrs = [
      el.value,
      el.getAttribute('value'),
      el.dataset.sellingPlanId,
      el.dataset.sellingPlan,
      el.dataset.planId,
      el.dataset.sellingplan,
    ];

    if (attrs.some((value) => valueMatchesPlanIds(value, config.planIds))) return true;

    const text = (el.textContent || '').toLowerCase();
    return config.groupNames.some((name) => name && text.includes(name));
  }

  function hidePlanElement(el) {
    if (el.matches('option')) {
      el.hidden = true;
      el.disabled = true;
      el.setAttribute('data-byb-plan-hidden', '');
      return;
    }

    const subscriptionRow = el.closest('.stayai-subscription, [data-selling-plan-id]');
    if (
      subscriptionRow &&
      !subscriptionRow.querySelector('.subscription-label, .one-time-purchase-label')
    ) {
      subscriptionRow.setAttribute('data-byb-plan-hidden', '');
      if ('disabled' in el && el.type !== 'hidden') el.disabled = true;
      return;
    }

    const purchaseLabel = el.closest('.stayai-purchase-label');
    if (purchaseLabel) {
      const isMainSubscribe = purchaseLabel.querySelector('.subscription-label');
      const isOneTime = purchaseLabel.querySelector('.one-time-purchase-label');
      if (isMainSubscribe || isOneTime) {
        if (el.name === 'selling_plan' || el.hasAttribute('data-selling-plan-id')) {
          el.setAttribute('data-byb-plan-hidden', '');
          if ('disabled' in el && el.type !== 'hidden') el.disabled = true;
        }
        return;
      }
      purchaseLabel.setAttribute('data-byb-plan-hidden', '');
      return;
    }

    el.setAttribute('data-byb-plan-hidden', '');
    if ('disabled' in el && el.type !== 'hidden') el.disabled = true;
  }

  function setControlValue(el, value) {
    if (!el || value == null) return;
    if (el.tagName === 'SELECT') {
      const option = Array.from(el.options).find((opt) => (
        normalizePlanId(opt.value) === value || String(opt.value).includes(value)
      ));
      if (!option) return;
      el.value = option.value;
      option.selected = true;
    } else if (el.type === 'radio') {
      el.disabled = false;
      el.checked = true;
    } else {
      el.value = value;
    }
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function applyAllowedSellingPlan(config) {
    const allowedId = config.allowedPlanIds[0];
    const sellingPlanFields = document.querySelectorAll(
      'input[name="selling_plan"], input[name*="selling_plan"], select[name="selling_plan"], select[name*="selling_plan"]'
    );

    const subscribeSelected = document.querySelector(
      '.stayai-purchase-label:has(.subscription-label) input[type="radio"]:checked, input[type="radio"][value*="subscription"]:checked'
    );
    const oneTimeRadio = document.querySelector(
      '.stayai-purchase-label:has(.one-time-purchase-label) input[type="radio"]'
    );

    if (!allowedId) {
      if (subscribeSelected && oneTimeRadio && !oneTimeRadio.checked) {
        oneTimeRadio.disabled = false;
        oneTimeRadio.checked = true;
        oneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    sellingPlanFields.forEach((field) => {
      if (field.type === 'radio') {
        if (valueMatchesPlanIds(field.value, new Set([allowedId]))) {
          setControlValue(field, allowedId);
        }
        return;
      }

      const currentId = normalizePlanId(field.value);
      if (!currentId || config.planIds.has(currentId)) {
        setControlValue(field, allowedId);
      }
    });

    document.querySelectorAll('select').forEach((select) => {
      const hasHidden = Array.from(select.options).some((opt) => valueMatchesPlanIds(opt.value, config.planIds));
      if (!hasHidden) return;
      const currentId = normalizePlanId(select.value);
      if (!currentId || config.planIds.has(currentId)) {
        setControlValue(select, allowedId);
      }
    });
  }

  function hideBundlePlans(config) {
    const roots = document.querySelectorAll('.stayai-widget, [id*="stay_buybox"], form[action*="/cart/add"]');
    if (!roots.length) return false;

    let hiddenSelected = false;

    roots.forEach((root) => {
      const candidates = root.querySelectorAll(
        'input, option, select, [data-selling-plan-id], [data-selling-plan], [data-plan-id], .stayai-subscription'
      );

      candidates.forEach((el) => {
        if (el.hasAttribute('data-byb-plan-hidden')) return;
        if (!elementMatchesHiddenPlan(el, config)) return;

        const wasSelected = Boolean(el.selected || el.checked || (
          (el.name === 'selling_plan' || (el.getAttribute('name') || '').includes('selling_plan')) &&
          valueMatchesPlanIds(el.value, config.planIds)
        ));

        hidePlanElement(el);
        if (wasSelected) hiddenSelected = true;
      });
    });

    if (hiddenSelected) {
      applyAllowedSellingPlan(config);
    } else {
      const sellingPlanField = document.querySelector('input[name="selling_plan"], select[name="selling_plan"]');
      if (sellingPlanField && valueMatchesPlanIds(sellingPlanField.value, config.planIds)) {
        applyAllowedSellingPlan(config);
      }
    }

    return true;
  }

  function initPdpPlanFilter() {
    const config = getHiddenPlansConfig();
    if (!config) return;

    let filtering = false;
    let scheduled = null;

    const run = () => {
      if (filtering) return;
      filtering = true;
      try {
        hideBundlePlans(config);
      } finally {
        filtering = false;
      }
    };

    const schedule = () => {
      if (scheduled != null) return;
      scheduled = requestAnimationFrame(() => {
        scheduled = null;
        run();
      });
    };

    run();

    const observer = new MutationObserver(() => {
      if (filtering) return;
      schedule();
    });

    const observeTarget = document.querySelector('.product-details') || document.body;
    observer.observe(observeTarget, { childList: true, subtree: true });

    document.addEventListener('variant:change', schedule);
    document.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.matches('[name="id"], [name="selling_plan"], [name*="selling_plan"], input[type="radio"]')) {
        schedule();
      }
    });

    [300, 800, 1600].forEach((ms) => setTimeout(run, ms));
  }

  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        initPdpPlanFilter();
        setupListeners();
      });
    } else {
      initPdpPlanFilter();
      setupListeners();
    }

    // Delayed check for dynamically loaded Stay AI widget
    setTimeout(() => {
      setupListeners();
      processInitialSelection();
    }, 1000);
  }

  init();
})();
